import React, { useMemo, useState } from 'react'
import { useTranslation } from 'language/translator'

import { TWidgetSettings, WidgetType } from 'types/GlobalWidget'

import Link from 'components_new/atoms/Link'
import Paper from 'components_new/atoms/Paper'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsPopup from 'components_new/molecules/SettingsPopup'

import {
  FilterConditionFormatBody,
  FilterPatchBody
} from 'types/GlobalKpiOption'
import type { TAttributeOptionDto } from '@homepal/api-contract'

import FilterGroup from './FilterGroup'
import FilterItem from './FilterItem'
import FilterGroupDialog from './FilterGroupDialog'
import NativeFilterSection from './NativeFilterSection'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

interface FilterSectionProps {
  filterAttributeOptions: TAttributeOptionDto[]
  filters: FilterConditionFormatBody[]
  id: string
  onClose: () => void
  settings: TWidgetSettings
  skipHelperText?: boolean
  updateFilters: (filters: FilterPatchBody[]) => void
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.BUBBLE_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.KEY_FIGURE,
  WidgetType.LINE_CHART,
  WidgetType.MAP,
  WidgetType.LIST,
  WidgetType.PIE_CHART,
  WidgetType.FUNNEL
]

const FilterSection = (props: FilterSectionProps) => {
  const {
    filterAttributeOptions,
    filters,
    id,
    onClose,
    settings,
    skipHelperText = false,
    updateFilters
  } = props

  const [addPopupOpen, setAddPopupOpen] = useState<boolean>(false)
  const [groupDialogOpen, setGroupDialogOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  const filterGroups = useMemo(() => {
    const groups: {
      [groupId: string]: {
        filterGroupId?: string | null
        filters: FilterConditionFormatBody[]
      }
    } = {}

    filters
      .filter((filter) => filter.condition.selected)
      .forEach((filter, i) => {
        const group = filter.filter_group_id || i

        if (group in groups) {
          groups[group].filters.push(filter)
        } else {
          groups[group] = {
            filterGroupId: filter.filter_group_id,
            filters: [filter]
          }
        }
      })

    return Object.values(groups).sort((a, b) =>
      sortAlphabeticalAsc(
        a.filters[0].attribute_option.selected,
        b.filters[0].attribute_option.selected
      )
    )
  }, [filters])

  if (!LEGAL_TYPES.includes(settings.type.selected)) {
    return null
  }

  const groupContent = (
    <>
      {filterGroups.length === 0 ? (
        <SettingsItem>
          <EmptyPlaceholder
            background={true}
            sx={{ width: '100%' }}
            title={t('filters', 'no_active_filters')}
          />
        </SettingsItem>
      ) : (
        filterGroups.map((group, i) => {
          const groupFilters = group.filters
          const isGrouped = !!group.filterGroupId && groupFilters.length > 1

          if (isGrouped) {
            return (
              <FilterGroup
                allFilters={filters}
                filterAttributeOptions={filterAttributeOptions}
                filters={groupFilters}
                groupId={group.filterGroupId as string}
                key={`filter-group-${i}`}
                onClose={onClose}
                updateFilters={updateFilters}
              />
            )
          } else {
            return (
              <FilterItem
                allFilters={filters}
                filterAttributeOptions={filterAttributeOptions}
                filter={groupFilters[0]}
                key={`filter-${i}`}
                onClose={onClose}
                updateFilters={updateFilters}
              />
            )
          }
        })
      )}

      <SettingsItem
        color="success"
        onClick={() => setAddPopupOpen(true)}
        title={t('filters', 'add_filter')}
        variant="add"
      />

      <SettingsPopup
        disabledTitle={t('widgets', 'select_value_to_add')}
        onCancel={() => setAddPopupOpen(false)}
        onClose={onClose}
        open={addPopupOpen}
        title={t('filters', 'new_filter')}
      >
        <Paper sx={{ p: 2 }} variant="outlined">
          <NativeFilterSection
            filterAttributeOptions={filterAttributeOptions}
            filters={filters}
            id={id}
            onSubmit={() => setAddPopupOpen(false)}
            updateFilters={(_, body) => updateFilters(body.filters)}
          />
        </Paper>
      </SettingsPopup>
    </>
  )

  return (
    <>
      <SettingsGroup
        helperText={
          skipHelperText ? (
            <Link onClick={() => setGroupDialogOpen(true)}>
              {t('filters', 'add_filter_group')}
            </Link>
          ) : (
            <>
              {t('filters', 'all_criteria_met_prefix')}{' '}
              <b>{t('filters', 'all')}</b> {t('filters', 'criteria_met_suffix')}
              <br />
              {t('filters', 'want_any_criteria_prefix')}{' '}
              <b>{t('filters', 'one_of')}</b>{' '}
              {t('filters', 'criteria_question_suffix')}{' '}
              <Link onClick={() => setGroupDialogOpen(true)}>
                {t('filters', 'add_filter_group')}
              </Link>
              .
            </>
          )
        }
        title={t('filters', 'filters')}
      >
        {groupContent}
      </SettingsGroup>

      {/*-- dialogs --*/}
      <FilterGroupDialog
        allFilters={filters}
        filterAttributeOptions={filterAttributeOptions}
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        updateFilters={updateFilters}
      />
    </>
  )
}

export default FilterSection
